import { defineStore } from 'pinia'
import { useAuth } from './auth'
import { EntityType } from '~/models/common'
import type { Comment } from '~/models/comments'
import type { UserBasicData } from '~/models/users'

type List = ReturnType<typeof useInfinityList<Comment>>

interface CommentsState {
  comments: Record<number, List | undefined>
}

export const useComments = defineStore('comments', () => {
  const baseURL = useRuntimeConfig().public.gatewayApi
  const adminURL = useRuntimeConfig().public.gatewayAdminApi
  const authStore = useAuth()

  const state = reactive<CommentsState>({
    comments: {},
  })

  function initCommentsList(
    id: number,
    entityType: EntityType,
    parent = 0, // use 0 as id for main comments branch
    perPage = 50,
    page = 1
  ) {
    if (state.comments[0]?.state.params.entity_id !== id) {
      state.comments = {}
    } else if (state.comments[parent]) {
      return
    }

    state.comments[parent] = useInfinityList<Comment>(
      '/comments',
      baseURL,
      () => undefined
    )

    state.comments[parent]!.state.params = {
      page,
      per_page: perPage,
      ...(parent && { parent_id: parent }),
      entity_type: entityType,
      entity_id: id,
    }
  }

  async function createComment(
    id: number,
    text: string,
    entityType: EntityType,
    parent?: number,
    replyTo?: number
  ) {
    const { data } = await useAuthFetch<{ data: Comment }>('/comments', {
      baseURL,
      method: 'POST',
      body: {
        entity_id: id,
        entity_type: entityType,
        text,
        ...(parent && { parent_id: parent }),
        ...(replyTo && { reply_to_comment_id: replyTo }),
      },
    })

    const commentsList = state.comments[parent || 0]

    if (commentsList) {
      data.user = authStore.currentUserBasicData

      if (replyTo) {
        const replyToIndex = commentsList.state.hits.findIndex(
          (comment) => comment.id === replyTo
        )

        commentsList.state.hits.splice(
          replyToIndex < 0 ? 0 : replyToIndex + 1,
          0,
          data
        )
      } else {
        commentsList.state.hits.unshift(data)
      }
    }
  }

  async function deleteComment(
    id: number,
    parent = 0,
    isEditByAdmin: boolean,
    initialUser: UserBasicData | null
  ) {
    const { data } = await useAuthFetch<{ data: Comment }>(`/comments/${id}`, {
      baseURL: isEditByAdmin ? adminURL : baseURL,
      method: 'DELETE',
    })

    if (!state.comments[parent]) {
      throw new Error(
        'Error on delete comment from state, comments branch not loaded'
      )
    }

    const commentIdx = state.comments[parent]?.state.hits.findIndex(
      (comment) => comment.id === id
    )

    if (commentIdx === undefined || isNaN(commentIdx) || commentIdx < 0) {
      throw new Error(
        'Error on delete comment from state, no comment with such id in the state'
      )
    }

    if (initialUser) {
      data.user = initialUser
    } else {
      data.user = authStore.currentUserBasicData
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { childs_count: _dropFreshCount, ...deletedComment } = data

    state.comments[parent]!.state.hits[commentIdx] = {
      ...state.comments[parent]!.state.hits[commentIdx],
      ...deletedComment,
    }
  }

  async function editComment(
    id: number,
    text: string,
    parent: number,
    isEditByAdmin: boolean,
    initialUser: UserBasicData | null
  ) {
    const { data } = await useAuthFetch<{ data: Comment }>(`/comments/${id}`, {
      baseURL: isEditByAdmin ? adminURL : baseURL,
      method: 'PATCH',
      body: {
        text,
      },
    })

    if (!state.comments[parent]) {
      throw new Error(
        'Error on edit comment in state, comments branch not loaded'
      )
    }

    const commentIdx = state.comments[parent]?.state.hits.findIndex(
      (comment) => comment.id === id
    )

    if (commentIdx === undefined || isNaN(commentIdx) || commentIdx < 0) {
      throw new Error(
        'Error on edit comment in state, no comment with such id in the state'
      )
    }

    if (initialUser) {
      data.user = initialUser
    } else {
      data.user = authStore.currentUserBasicData
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { childs_count: _dropFreshCount, ...editedComment } = data

    state.comments[parent]!.state.hits[commentIdx] = {
      ...state.comments[parent]!.state.hits[commentIdx],
      ...editedComment,
    }
  }

  function getReplyComment(id: number, parent = 0) {
    return state.comments[parent]?.state.hits.find(
      (comment) => comment.id === id
    )
  }

  return {
    initCommentsList,
    createComment,
    deleteComment,
    editComment,
    getReplyComment,
    comments: toRef(state, 'comments'),
  }
})
